/**
 * @fileoverview Unified result display management class.
 * Handles rendering search results, storing entries in localStorage, and providing filtering/pagination functionality.
 * @module ui/Lister
 */


import { restartCssFadeAnimation, removeCssFadeAnimation, pluralize, buildEmptySearchPack, escapeHtml, compareItemsIgnoreFields, formatTimeAgo, splitDNameIntoBaseAndSuffix, getOwnValue, anyFieldMatches } from '../utils/jrfuncs.js';
import { preProcessQueryParseOptionsDirectives } from '../utils/jrdslquery.js';
import { ICONS, RESULT_MANAGER_CONSTANTS, PTYPES, SEARCH_TYPE_LABELS, DOM_ELEMENTS, FIELDS_IGNORE_FOR_DUPEADD, FIELDS_PRESUMPTIVE_MATCH_FOR_DUPEADD, DEBUG_REFRESH, MAP_CONSTANTS } from '../constants.js';

const { CLASS_LISTER_ITEM, CLASS_LISTER_HEADER, CLASS_LISTER_CONTENT, CLASS_LISTER_COUNT,
    CLASS_LISTER_EMPTY, CLASS_LISTER_CLEAR_ALL_BTN } = DOM_ELEMENTS;
import { formatAddressForItemNicelyCheckOverride } from '../utils/nynfuncs.js';
import { itemHasPointGeometry } from '../utils/jrgeofuncs.js';
import { HtmlBuilder } from '../utils/HtmlBuilder.js';



/**
 * Unified class for displaying and managing results/entries.
 * Each tab can instantiate its own Lister with independent
 * configuration, data storage, and rendering. Supports both
 * database search results and locally stored entries (bookmarks, history).
 * @class
 */
export class Lister {
    /**
     * Creates a new Lister instance.
     * @param {Object} parentSearcher - The parent search searcher instance (has mapApp reference)
     * @param {string|null} containerId - ID of container element (null for data-only)
     * @param {Object} [config={}] - Configuration options
     * @param {string} [config.emptyMessage] - Message when no results and no search
     * @param {string} [config.noResultsMessage] - Message when search returns no results
     * @param {string} [config.loadingMessage] - Message during loading state
     * @param {boolean} [config.showCount=true] - Whether to show result count
     * @param {boolean} [config.showClearAllButton=false] - Whether to show clear all button
     * @param {string} [config.style='default'] - Style identifier
     * @param {string} [config.resultTerm='result'] - Term for result items (for pluralization)

     */
    constructor(parentSearcher, containerId, config = {}) {
        // Parent searcher reference (provides access to mapApp and stateManager)
        this.parentSearcher = parentSearcher;
        this.mapApp = parentSearcher.mapApp;

        // Container setup (optional - null for data-only management)
        this.containerId = containerId;
        this.container = containerId ? document.getElementById(containerId) : null;

        if (containerId && !this.container) {
            console.error(`ERROR: Lister - Container with id "${containerId}" not found`);
        }

        // Configuration
        this.config = {
            // Display options
            emptyMessage: config.emptyMessage || RESULT_MANAGER_CONSTANTS.DEFAULT_EMPTY_RESULTS_MESSAGE,
            noResultsMessage: config.noResultsMessage || RESULT_MANAGER_CONSTANTS.DEFAULT_NO_RESULTS_MESSAGE,
            loadingMessage: config.loadingMessage || 'Loading...',
            showCount: config.showCount !== undefined ? config.showCount : true,
            showClearAllButton: config.showClearAllButton || false,

            style: config.style || 'default',
            resultTerm: config.resultTerm || 'item',

            //
            contentsLabel: config.contentsLabel || 'results',
            supportsIndent: config.supportsIndent || true,

            ...config
        };

        // Display state
        this.results = [];
        this.isLoading = false;
        this.setLastSearchPack(buildEmptySearchPack());

        // state management (set to true by Searcher.loadResultsFromStorage when data is loaded)
        this.loaded = !(this.parentSearcher.relStorageKey);

        // Render if container exists
        this.createResultContainer();

        // one delegated set of item click/keyboard listeners on the container (removed in destroy)
        this.delegatedItemHandlerRemovers = [];
        this.bindDelegatedItemHandlers();
    }








    
    // ==========================================
    // SETUP / INIT HELPERS
    // ==========================================


    /**
     * Sets up the Clear All button click handler with confirmation dialog.
     * @private
     */
    setupClearAllButton() {
        if (!this.requireContainer() || !this.config.showClearAllButton) return;

        if (this.clearAllBtnEl) {
            this.clearAllBtnEl.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.parentSearcher.confirmThenClearAllResults();
            });
        }
    }

    /**
     * Binds the click / dblclick / keydown handling for result items: ONE delegated listener of
     * each kind on the container, serving every .lister-item rendered into it (it used to add three
     * listeners to every item after every render). Clicking an item sets it as the selected result.
     * IMPORTANT: A doubleclick will first trigger a single click and then the double click will follow; debouncing introduced to large a UI delay
     * The container is static page DOM and a world switch builds a new Lister on it, so the
     * listeners are removed again in destroy().
     * @private
     */
    bindDelegatedItemHandlers() {
        if (!this.requireContainer()) return;

        // the result item an event happened in (or null when it was outside any item)
        const findItemEl = (event) => {
            const itemEl = event.target?.closest ? event.target.closest('.' + CLASS_LISTER_ITEM) : null;
            return (itemEl && this.container.contains(itemEl)) ? itemEl : null;
        };

        // sends a click on the item with this uid to our searcher
        const handleClickSendToOnItemClickCallback = (event, flagDoubleClick, uid) => {
            const result = this.getResultById(uid);
            this.parentSearcher.handleItemClick(this, result, event, flagDoubleClick);
        };

        const onClick = (event) => {
            const itemEl = findItemEl(event);
            if (!itemEl) return;
            // Skip the second click of a doubleclick (detail >= 2 means this is part of a multi-click)
            if (event.detail >= 2) return;
            // a single click, on its own or right before a double click
            handleClickSendToOnItemClickCallback(event, false, itemEl.dataset.uid || null);
        };

        const onDblClick = (event) => {
            const itemEl = findItemEl(event);
            if (!itemEl) return;
            handleClickSendToOnItemClickCallback(event, true, itemEl.dataset.uid || null);
        };

        // keyboard navigation with arrow keys
        const onKeydown = (event) => {
            const itemEl = findItemEl(event);
            if (!itemEl) return;
            // a key pressed ON a control inside the card (an action-bar button, a link, an input)
            // belongs to that control: Enter/Space on a focused "bookmark" button must press it.
            // Handling it here prevented the button's own activation and ran the card's
            // double-click action instead (zoom to the item, or run a saved search).
            if ((event.target !== itemEl) && (event.target.closest('button, a[href], input, textarea, select, [contenteditable="true"]'))) {
                return;
            }
            const items = this.container.querySelectorAll('.' + CLASS_LISTER_ITEM);
            const index = Array.prototype.indexOf.call(items, itemEl);
            this.handleItemKeydown(event, index, handleClickSendToOnItemClickCallback);
        };

        const container = this.container;
        container.addEventListener('click', onClick);
        container.addEventListener('dblclick', onDblClick);
        container.addEventListener('keydown', onKeydown);
        this.delegatedItemHandlerRemovers = [
            () => container.removeEventListener('click', onClick),
            () => container.removeEventListener('dblclick', onDblClick),
            () => container.removeEventListener('keydown', onKeydown),
        ];
    }


    /**
     * Finds the rendered element of the result item with this uid inside our container.
     * The uid is css-escaped: uids are data (they contain quotes, ampersands, spaces...).
     * @param {string} uid - The item uid
     * @returns {HTMLElement|null}
     * @private
     */
    findItemElByUid(uid) {
        if (!this.container || uid === null || uid === undefined) return null;
        return this.container.querySelector(`[data-uid="${CSS.escape(String(uid))}"]`);
    }





    /**
     * Renders the result list container structure with header and content areas.
     * Called once during initialization. Creates the wrapper, header (with count and clear button),
     * and content area elements.
     * @private
     */
    createResultContainer() {
        if (!this.requireContainer()) return;

        const clearAllButtonHtml = this.config.showClearAllButton
            ? `<button class="btn btn-outline-secondary btn-sm ${CLASS_LISTER_CLEAR_ALL_BTN}" title="Clear all items" style="display: none; font-size: 0.7rem; padding: 0.15rem 0.5rem;">Clear All</button>`
            : '';

        this.container.innerHTML = `
            <div class="lister-wrapper">
                <div class="${CLASS_LISTER_HEADER}" style="display: none;">
                    <span class="${CLASS_LISTER_COUNT} jrfade-status"></span>
                    ${clearAllButtonHtml}
                </div>
                <div class="${CLASS_LISTER_CONTENT}">
                    <div class="${CLASS_LISTER_EMPTY}">${this.config.emptyMessage}</div>
                </div>
            </div>
        `;

        this.setupClearAllButton();
    }





    /**
     * Destroy the lister and clean up
     */
    destroy() {
        // the container is static page DOM that the next world's Lister reuses, so the delegated
        // listeners must go with us (idempotent: a failed world load can destroy a searcher twice)
        for (const remove of this.delegatedItemHandlerRemovers || []) {
            remove();
        }
        this.delegatedItemHandlerRemovers = [];
        if (this.container) {
            this.container.innerHTML = '';
        }
        this.results = [];
    }








    // ==========================================
    // ACCESSORS ETC.
    // ==========================================

    /**
     * Gets the parent searcher instance.
     * @returns {Object} The parent searcher
     */
    getSearcher() { return this.parentSearcher; }

    /**
     * Gets the MapApp instance.
     * @returns {Object} The MapApp instance
     */
    getMapApp() {
        return this.mapApp;
    }

    /**
     * Gets the StateManager instance via the parent searcher.
     * @returns {Object} The StateManager instance
     */
    getStateManager() {
        return this.parentSearcher.getStateManager();
    }

    /**
     * Get all results
     * @returns the results in the list
     */
    getInternalResults() {
        return this.results;
    }

    /**
     * Sets the internal results array directly.
     * @param {Array} results - The results array to set
     */
    setInternalResults(results) {
        this.results = results;
    }





    
    // ==========================================
    // MORE ACCESSORS ETC.
    // ==========================================



    /**
     * Cached getter for header element
     * @returns {HTMLElement|null}
     */
    get headerEl() {
        return this.container?.querySelector('.' + CLASS_LISTER_HEADER);
    }

    /**
     * Cached getter for content element
     * @returns {HTMLElement|null}
     */
    get contentEl() {
        return this.container?.querySelector('.' + CLASS_LISTER_CONTENT);
    }

    /**
     * Cached getter for count span element
     * @returns {HTMLElement|null}
     */
    get countSpanEl() {
        return this.headerEl?.querySelector('.' + CLASS_LISTER_COUNT);
    }

    /**
     * Cached getter for clear all button element
     * @returns {HTMLElement|null}
     */
    get clearAllBtnEl() {
        return this.headerEl?.querySelector('.' + CLASS_LISTER_CLEAR_ALL_BTN);
    }

    /**
     * Guard method - returns true if container exists, false otherwise
     * @returns {boolean}
     */
    requireContainer() {
        return !!this.container;
    }


    /**
     * Checks if the current results are from a completed search.
     * @returns {boolean} True if a search has been performed
     */
    getHasSearched() {
        // ATTN: new we check waitingOnEnter
        return this.lastSearchPack.hasSearched && !this.lastSearchPack.waitingOnEnter;
    }

    /**
     * Checks if a search is pending, waiting for Enter key to execute.
     * @returns {boolean} True if waiting on Enter key press
     */
    getHasSearchedWaitingOnEnter() {
         return this.lastSearchPack.waitingOnEnter;
    }

    /**
     * Get display result count
     * @returns {number} Number of display results
     */
    getResultCount() {
        return this.results.length;
    }

    /**
     * Gets the timestamp field name from the parent searcher.
     * @returns {string|null} The field name used for timestamps, or null
     */
    getTimeStampField() {
        return this.parentSearcher.getTimeStampField();
    }






    /**
     * Check if an item exists
     * @param {string} uid - item uid
     * @returns {boolean} True if item exists
     */
    hasResultWithId(uid) {
        return this.results.some(item => item.uid === uid);
    }

    /**
     * Get a specific item by uid
     * @param {string} uid - item uid
     * @returns {Object|null} item or null if not found
     */
    getResultById(uid) {
        return this.results.find(item => item.uid === uid) || null;
    }


    /**
     * Stores the most recent search pack for reference.
     * @param {Object} searchPack - The search pack to store
     */
    setLastSearchPack(searchPack) {
        this.lastSearchPack = searchPack;
    }

    /**
     * Gets the most recently stored search pack.
     * @returns {Object} The last search pack
     */
    getLastSearchPack() {
        return this.lastSearchPack;
    }



















    // ==========================================
    // EVENT HANDLERS
    // ==========================================


    /**
     * Handles keydown events on result items for keyboard navigation.
     * Arrow Up/Down moves focus between items, Enter/Space triggers selection.
     * @param {KeyboardEvent} event - The keyboard event
     * @param {number} currentIndex - Index of the current item in results
     * @param {Function} clickCallback - Callback to trigger item selection
     * @private
     */
    handleItemKeydown(event, currentIndex, clickCallback) {
        const items = this.container.querySelectorAll('.' + CLASS_LISTER_ITEM);
        const itemCount = items.length;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (currentIndex < itemCount - 1) {
                    const nextItem = items[currentIndex + 1];
                    nextItem.focus();
                    nextItem.click();
                }
                break;

            case 'ArrowUp':
                event.preventDefault();
                if (currentIndex > 0) {
                    const prevItem = items[currentIndex - 1];
                    prevItem.focus();
                    prevItem.click();
                }
                break;

            case 'Enter':
            case ' ':
                // Space or Enter triggers double click
                event.preventDefault();
                const currentItem = items[currentIndex];
                const uid = currentItem.dataset.uid || null;
                clickCallback(event, true, uid);
                break;

            case 'Home':
                // Jump to first item
                event.preventDefault();
                if (itemCount > 0) {
                    items[0].focus();
                    items[0].click();
                }
                break;

            case 'End':
                // Jump to last item
                event.preventDefault();
                if (itemCount > 0) {
                    items[itemCount - 1].focus();
                    items[itemCount - 1].click();
                }
                break;
        }
    }
























    // ==========================================
    // RENDER HELPERS
    // ==========================================


    /**
     * Updates the result header with count information, error messages, and re-run links.
     * Shows/hides the header based on whether results exist or a search has been performed.
     * @param {Object} searchPack - The search pack with resultCount, hasSearched, error, etc.
     */
    updateResultHeaderWithCountAndErrorEtc(searchPack) {
        if (!this.config.showCount) {
            return;
        }
        if (!this.countSpanEl) {
            return;
        }

        // show # of results?
        if ((searchPack.resultCount > 0) || (searchPack.hasSearched) || (searchPack.error)) {
            // show header
            this.headerEl.style.display = 'flex';
                const countMessageHtml = this.buildResultHeaderCountMessageHtml(searchPack);
                // when the count result text stays same we give it a nice little fade in out effect so user can see that we are updating when they type even if results dont change
                if (this.countSpanEl.innerHTML == countMessageHtml) {
                    restartCssFadeAnimation(this.countSpanEl);
                }
                this.countSpanEl.innerHTML = countMessageHtml;
                if (true) {
                    const reRunLink = this.countSpanEl.querySelector('.re-run-link');
                    if (reRunLink) {
                        reRunLink.addEventListener('click', (e) => {
                            e.preventDefault();
                            this.parentSearcher.reRunSearchPack(searchPack);
                        });
                    }
                }
        } else {
            // hide result count bar
            this.headerEl.style.display = 'none';
            if (this.countSpanEl) {
                this.countSpanEl.innerHTML = '';
                removeCssFadeAnimation(this.countSpanEl);
            }
        }

    }



    /**
     * Build the count text for the header
     * @returns {string} HTML string for count display
     */
    buildResultHeaderCountMessageHtml(searchPack) {
        //searchPack = this.lastSearchPack;
        let retvHtml;
        let textForReRunLabel= null;
        //
        if (searchPack.waitingOnEnter) {
            retvHtml = 'Hit Enter to search';
            textForReRunLabel = RESULT_MANAGER_CONSTANTS.RERUN_LABEL_WAITINGONENTER;
        } else if (searchPack.truncated || searchPack.random) {
            retvHtml = `${searchPack.resultCount} of ${searchPack.totalCount} ${pluralize(searchPack.totalCount, this.config.resultTerm)}`;
            if (this.parentSearcher.getIsDbSearcher()) {
                textForReRunLabel = RESULT_MANAGER_CONSTANTS.RERUN_LABEL_SHOWALL;
                // a bit of a kludge but we want to see if this search has directives that limit and randomize the results, in which case we want to show that re-running it will randomize results not show all.
                // Parsed into a COPY: the parser rewrites the pack it is given (error, query, limit,
                // jregion), and running it on the stored pack replaced an evaluation error -- the
                // message shown below -- with the parser's own result (null).
                const parsedPack = { ...searchPack };
                preProcessQueryParseOptionsDirectives(parsedPack);
                if (searchPack.random || parsedPack.random) {
                    // yes show re-randomize and text of re-run button
                    textForReRunLabel = RESULT_MANAGER_CONSTANTS.RERUN_LABEL_RERANDOMIZE;
                }
            } else {
                textForReRunLabel = RESULT_MANAGER_CONSTANTS.RERUN_LABEL_CLEARFILTER;
            }
        } else {
            // complete results shown
            retvHtml = `${this.results.length} ${pluralize(this.results.length, this.config.resultTerm)}`;
        }

        // re-run link?
        if (textForReRunLabel) {
            retvHtml += ` (<a href="#" class="re-run-link">${textForReRunLabel}</a>)`;
        }

        // add error
        if (searchPack.error) {
            const errorHtml = escapeHtml(searchPack.error, true);
            retvHtml += `\n<div class="searchHeaderErrorMessage text-danger">${errorHtml}</div>`;
        }

        // return it
        return retvHtml;
    }






    // ==========================================
    // MISCELANEOUS UI HELPERS
    // ==========================================


    /**
     * Displays loading spinner state in the content area.
     */
    async showLoading() {
        if (!this.requireContainer()) return;
        this.isLoading = true;
        if (this.contentEl) {
            this.contentEl.innerHTML = `
                <div class="lister-loading text-muted text-center p-4">
                    <div class="spinner-border spinner-border-sm me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    ${this.config.loadingMessage}
                </div>
            `;
        }

        // give the browser a chance to paint
        // Need double RAF: first schedules the frame, second ensures paint completes
        await new Promise(requestAnimationFrame);
        await new Promise(requestAnimationFrame);
    }







    // ==========================================
    // MAIN METHODS
    // ==========================================


    /**
     * Sets all results from a PoiDataManager search result object.
     * Extracts results, count, and truncation info from the search result.
     * @param {Object} searchResult - Search result from PoiDataManager
     * @param {Array} searchPack
     * @returns {*} Result of setAllResultsWithPlain
     */
    setAllResultsFromSearchPack(searchResult, searchPack) {
        // remember last searchPack (query, resultcount, etc.)
        this.setLastSearchPack(searchPack);

        // now remember and display
        return this.setLocalResultsAndRender(searchResult.results, searchPack);
    }


    /**
     * Update the result list with new data
     * @param {Array} results - Array of result items
     */
    setLocalResultsAndRender(results, searchPack) {
        // Capture the active selection from the current DOM BEFORE swapping in the new
        // results, so we can tell whether the user's active result survives this change.
        const previouslySelectedUid = this.getSelectedResultUid();

        // set and remember the local results
        this.results = results || [];
        // clear loading
        this.isLoading = false;
        // now display (renderTheseResults re-selects + scrolls to previouslySelectedUid if it still exists)
        this.renderAllCurrentResults(searchPack);

        // If the active result did NOT carry over into the new set, the results have
        // completely changed (e.g. a new Yellow Pages category was chosen). The innerHTML
        // swap preserves the scroll container's scrollTop, so the list would otherwise stay
        // stuck where it was scrolled to; reset it to the top. When the active result DID
        // survive, renderTheseResults already scrolled to it, so leave the scroll alone.
        if (!(previouslySelectedUid && this.hasResultWithId(previouslySelectedUid))) {
            const scrollEl = this.contentEl;
            if (scrollEl) {
                scrollEl.scrollTop = 0;
            }
        }
    }

    /**
     * Renders the current results array using existing searchSummaryData.
     */
    renderAllCurrentResults(searchPack) {
        this.isLoading = false;
        this.renderTheseResults(this.results, searchPack);
    }





    /**
     * This call says dont save overright them! just render 
     * @param {Object} searchResult - Search result from PoiDataManager
     * @param {Array} searchPack
     * @returns {*} Result of setAllResultsWithPlain
     */
    showFilteredResultsFromSearchPackDontSaveLocally(searchResult, searchPack) {
        // remember last searchPack (query, resultcount, etc.)
        this.setLastSearchPack(searchPack);

        this.isLoading = false;
        this.renderTheseResults(searchResult.results, searchPack);
    }


















    // ==========================================
    // SELECTION MANAGEMENT METHODS
    // ==========================================

    /**
     * Get the data-uid of the currently selected result item.
     * @returns {string|null} The uid of the selected item, or null if none selected
     */
    getSelectedResultUid() {
        const selectedItemEl = this.container.querySelector(`.${RESULT_MANAGER_CONSTANTS.SELECTED_RESULT_CLASS}`);
        if (!selectedItemEl) {
            return null;
        }
        const uid = selectedItemEl.dataset.uid || null;
        return uid;
    }

    /**
     * Get the data-uid of the currently selected result item.
     * @returns {string|null} The uid of the selected item, or null if none selected
     */
    getSelectedResultItemAndUid() {
        const uid = this.getSelectedResultUid()
        if (!uid) {
            return {uid: null, item: null};
        }
        const item = this.results.find(r => r.uid === uid);
        if (!item) {
            console.error('ERROR: In getSelectedResultItemAndUid we got a non-null uid, but could not find the item in our result list.')
            return {uid: null, item: null};
        }
        return {uid: uid, item:item};
    }



    /**
     * Toggle the selected result by its data-uid.
     * If the item is already selected, clears the selection. Otherwise, selects it.
     * @param {string} uid - The uid of the item to toggle
     * @param {boolean} [fromMapMarkerClick=false] - True if triggered by a map marker click
     * @returns {boolean} True if the item is now selected, false if it was deselected or not found
     */
    toggleSelectedResultByUid(uid) {

        const currentSelectedUid = this.getSelectedResultUid();
        if (currentSelectedUid === uid) {
            // Already selected - deselect it
            this.clearSelectedResult();
            return false;
        } else {
            // Not selected - select it
            this.expandResultByUid(uid, true);
        }
    }

    /**
     * Force expand it if not already expanded
     * @param {string} uid - The uid of the item to toggle
     */
    expandResultByUid(uid, flagUserInitiated) {
        const currentSelectedUid = this.getSelectedResultUid();
        if (currentSelectedUid != uid) {
            // Not selected - select it; i THINK this sould only happen on user interaction so i think we dont want any moving/scrolling, but we will trust caller
            const flagScrollIntoView = false;
            const flagFromMarkerClick = false;
            const flagSmoothAnimate = false;
            const flagFlashMarker = true;
            return this.setSelectedResultOnListerByUid(uid, flagScrollIntoView, flagFromMarkerClick, flagSmoothAnimate, flagFlashMarker, flagUserInitiated);
        }
    }


    /**
     * Set the selected result by its data-uid.
     * Clears any previous selection and selects the item with the matching uid.
     * @param {string} uid - The uid of the item to select
     * @param {boolean} [scrollIntoView=true] - Whether to scroll the item into view
     * @param {boolean} [fromMapMarkerClick=false] - True if triggered by a map marker click
     * @returns {boolean} True if an item was selected, false if uid not found
     */
    setSelectedResultOnListerByUid(uid, flagScrollIntoView, fromMapMarkerClick, flagSmoothAnimate, flagFlashMarker, flagUserInitiated) {
        // NOTE that this may be called on a new result set where our item needs expanding even if it was previously set
        if (DEBUG_REFRESH) {
            console.log(`Lister for ${this.getSearcher().searcherName} is setSelectedResultOnListerByUid ${uid}.`)
        }

        //
        // Clear any existing selection first
        // ATTN: 1/30/26 -- WHY do we have to clear it first?
        const previouslySelectedUid = this.getSelectedResultUid();
        this.clearSelectedResult();

        const retv = this.scrollToItemUid(uid, flagScrollIntoView, fromMapMarkerClick, flagSmoothAnimate, flagFlashMarker, previouslySelectedUid, flagUserInitiated);
        return retv;
    }


    /**
     * Re-selects the currently selected result on the lister after a re-render.
     * Scrolls back to the previously selected item if one exists.
     */
    reSelectCurrentResultOnLister() {
        if (DEBUG_REFRESH) {
            console.log(`Lister for ${this.getSearcher().searcherName} is trying to go back to previously selected result.`)
        }
        const previouslySelectedUid = this.getSelectedResultUid();
        if (previouslySelectedUid) {
            this.scrollToItemUid(previouslySelectedUid, true, false, false, false, previouslySelectedUid, false);
        }
    }


    /**
     * Scrolls to and selects an item in the lister by its uid.
     * Adds the selected class, optionally scrolls into view, expands the action bar,
     * and notifies MapApp of the selection.
     * @param {string} uid - The uid of the item to scroll to
     * @param {boolean} flagScrollIntoView - Whether to scroll the item into view
     * @param {boolean} fromMapMarkerClick - True if triggered by a map marker click
     * @param {boolean} flagSmoothAnimate - Whether to use smooth scroll animation
     * @param {boolean} flagFlashMarker - Whether to flash the map marker
     * @param {string|null} previouslySelectedUid - The uid of the previously selected item
     * @returns {boolean} True if the item was found and selected, false otherwise
     * @private
     */
    scrollToItemUid(uid, flagScrollIntoView, fromMapMarkerClick, flagSmoothAnimate, flagFlashMarker, previouslySelectedUid, flagUserInitiated) {
        // Find the item with matching data-uid
        if (DEBUG_REFRESH) {
            console.log(`In searcher ${this.getSearcher().searcherName} triggering a scrollToItem ${uid}.`)
        }
        const itemEl = this.findItemElByUid(uid);
        if (itemEl) {
            itemEl.classList.add(RESULT_MANAGER_CONSTANTS.SELECTED_RESULT_CLASS);
            if (flagScrollIntoView) {
                // Scroll within the lister container only, to avoid the browser
                // scrolling ancestor elements (body) which shifts the viewport.
                this.scrollElementIntoContainerClaude(itemEl, flagSmoothAnimate);
                //this.scrollElementIntoContainerOrig(itemEl, flagSmoothAnimate);
            }
            // Find the result data for this uid
            const item = this.results.find(r => r.uid === uid);

            // expand it
            this.selectedResultExpand(itemEl, item, uid, flagFlashMarker);

            // notify centralized MapApp of the selection
            // ATTN: do we want to only trigger this if the previously selected uid has changed? or let caller worry about this
            if (previouslySelectedUid!=uid) {
                this.getMapApp().onItemSelected(uid, item, this, fromMapMarkerClick, flagFlashMarker, flagUserInitiated);
            }

            // success
            return true;
        }
        return false;
    }


    /**
     * Scrolls an element into view within this.container only, without
     * triggering ancestor (body) scrolling. Centers the element vertically
     * in the container's visible area.
     * @param {HTMLElement} itemEl - The element to scroll into view
     * @param {boolean} flagSmooth - Whether to use smooth scroll animation
     * @private
     */
    scrollElementIntoContainerClaude(itemEl, flagSmooth) {
        // The actual scrollable element is .lister-content (overflow-y: auto),
        // which is a child of this.container. We scroll that directly to avoid
        // scrollIntoView() leaking scroll to ancestor elements like body.
        const scrollParent = this.contentEl;
        if (!scrollParent) return;
        const parentRect = scrollParent.getBoundingClientRect();
        const itemRect = itemEl.getBoundingClientRect();
        // Calculate desired scroll to center the item in the visible area
        const offsetInParent = itemRect.top - parentRect.top + scrollParent.scrollTop;
        const desiredScrollTop = offsetInParent - (parentRect.height / 2) + (itemRect.height / 2);
        scrollParent.scrollTo({
            top: desiredScrollTop,
            behavior: flagSmooth ? 'smooth' : 'auto'
        });
    }

    /**
     * Scrolls an element into view within this.container only, without
     * triggering ancestor (body) scrolling. Centers the element vertically
     * in the container's visible area.
     * @param {HTMLElement} itemEl - The element to scroll into view
     * @param {boolean} flagSmooth - Whether to use smooth scroll animation
     * @private
     */
    scrollElementIntoContainerOrig(itemEl, flagSmooth) {
            if (flagSmooth) {
                itemEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
                itemEl.scrollIntoView({ behavior: 'auto', block: 'center' });
                //itemEl.scrollIntoView({ behavior: 'auto', block: 'nearest' });
            }
    }





    /**
     * Just proxy to lister a question about ther the uid is in the lister
     * @param {string} uid - The unique identifier
     * @returns {boolean} True if found
     */
    hasResultItemOnListerByUid(uid) {
        const itemEl = this.findItemElByUid(uid);
        if (itemEl) {
            return true;
        }
        return false;
    }


    /**
     * Expands a selected result item by adding an action bar at the bottom.
     * Uses renderActionBar() to generate the HTML content.
     * Also expands any ad images found in the item.
     * @param {HTMLElement} itemEl - The result item DOM element
     * @param {Object} item - The result item data
     * @param {string} uid - The unique identifier of the result item
     * @param {boolean} flagFlashMarker - Whether to flash the map marker for this item
     */
    selectedResultExpand(itemEl, item, uid, flagFlashMarker) {
        // Remove any existing action bar first
        this.selectedResultCollapse(itemEl, false);

        // Generate action bar HTML using configured renderer
        const actionBarHtml = this.renderActionBar(item, uid);
        if (!actionBarHtml) return;

        // Check for existing ad image and create expanded version if present
        const existingAd = itemEl.querySelector('.poi-ad img');
        if (existingAd) {
            const expandedAd = document.createElement('div');
            expandedAd.className = 'poi-ad-expanded';
            // src/alt read back from the DOM are decoded text again, so re-escape them
            expandedAd.innerHTML = `<img src="${escapeHtml(existingAd.src)}" alt="${escapeHtml(existingAd.alt || '')}"/>`;
            itemEl.appendChild(expandedAd);
        }

        // Create and append the action bar
        const actionBar = document.createElement('div');
        actionBar.className = RESULT_MANAGER_CONSTANTS.ACTION_BAR_CLASS;
        actionBar.innerHTML = actionBarHtml;

        // Setup click handlers for action buttons
        this.setupActionBarClickHandlers(actionBar, item, uid);

        // add it
        itemEl.appendChild(actionBar);

        // tell the mapApp about this -- it will want to refresh the highlighted ring marker
        this.mapApp.onItemExpand(this, item, uid, flagFlashMarker);
    }


    /**
     * Collapses a selected result item by removing its action bar and expanded ad.
     * @param {HTMLElement} itemEl - The result item DOM element
     * @param {boolean} flagTriggerEvents - Whether to notify MapApp of the collapse event
     */
    selectedResultCollapse(itemEl, flagTriggerEvents) {
        // Remove expanded ad if present
        const expandedAd = itemEl.querySelector('.poi-ad-expanded');
        if (expandedAd) {
            expandedAd.remove();
        }

        const actionBar = itemEl.querySelector(`.${RESULT_MANAGER_CONSTANTS.ACTION_BAR_CLASS}`);
        if (actionBar) {
            actionBar.remove();

            if (flagTriggerEvents) {
                // tell the mapApp about this -- it will want to refresh the highlighted ring marker
                this.mapApp.onItemCollapse(this);
            }
        }
    }



















    /**
     * Clear the currently selected result.
     * Removes the selection styling from any selected item.
     */
    clearSelectedResult() {
        const selectedItems = this.container.querySelectorAll(`.${RESULT_MANAGER_CONSTANTS.SELECTED_RESULT_CLASS}`);
        selectedItems.forEach(item => {
            item.classList.remove(RESULT_MANAGER_CONSTANTS.SELECTED_RESULT_CLASS);
            this.selectedResultCollapse(item, true);
        });
    }









































    // ==========================================
    // ACTION BAR AND RELATED METHODS
    // ==========================================


    /**
     * Action configuration registry.
     * Defines how each action type determines its state, icons, and titles.
     * @private
     * @returns {Object} Map of action name to configuration object
     */
    getActionConfig() {
        return {
            bookmark: {
                checkStateIsActive: (item, uid) => this.mapApp?.isBookmarkedByUid(uid) || false,
                toggle: (uid, item) => this.mapApp?.actionOnItemToggleBookmark(uid, item, this),
                iconActive: ICONS.BOOKMARK_FILLED,
                iconInactive: ICONS.BOOKMARK,
                titleActive: 'Remove bookmark',
                titleInactive: 'Bookmark this result'
            },
            savesearch: {
                checkStateIsActive: (item, uid) => this.mapApp?.isSavedSearchByUid(uid) || false,
                toggle: (uid, item) => this.mapApp?.actionOnItemToggleSavedSearch(uid, item, this),
                iconActive: ICONS.SAVEDSEARCH_FILLED,
                iconInactive: ICONS.SAVEDSEARCH,
                titleActive: 'Remove saved search',
                titleInactive: 'Save this search'
            },
            edit: {
                checkStateIsActive: (item, uid) => item && item.isCustomized,
                toggle: async (uid, item) => await this.mapApp?.actionOnItemEdit(uid, item, this),
                iconInactive: ICONS.EDIT,
                iconActive: ICONS.EDIT_FILLED,
                titleInactive: 'Add bookmark details..',
                titleActive: 'Modify bookmark details..'
            },
            copyClipboard: {
                checkStateIsActive: (item, uid) => false,
                toggle: (uid, item) => this.mapApp?.actionOnItemCopyToClipboard(uid, item, this),
                iconInactive: ICONS.COPYCLIPBOARD,
                titleInactive: 'Copy item to clipboard',
            },
            readLead: {
                checkStateIsActive: (item, uid) => false,
                toggle: (uid, item) => this.mapApp?.actionOnItemReadLead(uid, item, this),
                iconInactive: ICONS.READLEAD,
                titleInactive: 'Lookup this lead in casebook',
            },
            extraActionsMenu: {
                checkStateIsActive: (item, uid) => false,
                toggle: (uid, item, event) => this.mapApp?.actionOnItemExtraActionsMenu(uid, item, this, event),
                iconInactive: ICONS.EXTRA_ACTION_MENU,
                titleInactive: 'Additional actions on this item',
            },
            delete: {
                checkStateIsActive: (item, uid) => false,
                toggle: (uid, item) => this.mapApp?.actionOnItemDelete(uid, item, this),
                iconInactive: ICONS.DELETE,
                titleInactive: 'Delete',
            },
            launchSearch: {
                checkStateIsActive: (item, uid) => false,
                toggle: (uid, item) => this.mapApp?.actionOnItemLaunchSearch(uid, item, this),
                iconInactive: ICONS.LAUNCH_SEARCH,
                titleInactive: 'Run search',
            },
            launchItem: {
                checkStateIsActive: (item, uid) => false,
                toggle: (uid, item) => this.mapApp?.actionOnItemLaunchItem(uid, item, this),
                iconInactive: ICONS.LAUNCH_ITEM,
                titleInactive: 'Show on map',
            },
            anchor: {
                checkStateIsActive: (item, uid) => this.mapApp?.isAnchoredByUid(uid) || false,
                toggle: (uid, item) => this.mapApp?.actionOnItemToggleAnchor(uid, item, this),
                iconActive: ICONS.ANCHOR_FILLED,
                iconInactive: ICONS.ANCHOR,
                titleActive: 'Remove anchor',
                titleInactive: 'Set as anchor'
            },
        };
    }

    /**
     * Gets icon and title for an action based on its current state.
     * @param {string} actionName - The action name (see SEARCH_TYPES)
     * @param {boolean} isActive - Whether the action is currently active
     * @returns {Object} Object with icon and title properties
     * @private
     */
    getActionDisplay(actionName, isActive) {
        const config = this.getActionConfig()[actionName];
        return {
            icon: isActive ? config.iconActive : config.iconInactive,
            title: isActive ? config.titleActive : config.titleInactive
        };
    }

    /**
     * Sets up click handlers for buttons within the action bar.
     * Buttons should have a data-action attribute specifying the action name.
     * Handles registered actions internally.
     * @param {HTMLElement} actionBar - The action bar DOM element
     * @param {Object} item - The result item data
     * @param {string} uid - The unique identifier of the result item
     * @private
     */
    setupActionBarClickHandlers(actionBar, item, uid) {
        const actionConfig = this.getActionConfig();
        const buttons = actionBar.querySelectorAll('[data-action]');

        buttons.forEach(button => {
            button.addEventListener('click', async (event) => {
                event.stopPropagation(); // Prevent triggering item click
                const action = button.dataset.action;
                const config = actionConfig[action];

                // Handle registered actions
                if (config?.toggle) {
                    const toggleResult = await config.toggle(uid, item, event);
                    // we check toggleResult because some toggles return a promise asynchronously and in that case we shouldnt update state of button we should let caller do it
                    if (toggleResult===true || toggleResult===false) {
                        const isNowActive = toggleResult;
                        const display = this.getActionDisplay(action, isNowActive);
                        // console.log(`After toggle active = ${isNowActive}.`)

                        // Update the button icon and title
                        const icon = button.querySelector('i');
                        if (icon && display.icon) {
                            icon.className = display.icon;
                        }
                        if (display.title) {
                            button.title = display.title;
                        }
                    }
                }
            });
        });
    }


    /**
     * Default action bar renderer that creates a centered row with action buttons.
     * Shows appropriate icon based on current state (e.g., filled if bookmarked).
     * @param {Object} item - The result item data
     * @param {string} uid - The unique identifier of the result item
     * @returns {string} HTML string for the action bar content
     */
    renderActionBar(item, uid) {
        // Determine which action(s) to show based on item type
        let actionNames = [];

        if (this.parentSearcher.enableActionBarEdit) {
            // edit notes for bookmaps and saved searches
            actionNames.push('edit');
        }
        //
        if (this.parentSearcher.enableActionBarSave) {
            if (item.ptype == PTYPES.SEARCH) {
                // add search or bookmark depending on type
                actionNames.push('savesearch');
            } else {
                // for search db items
                actionNames.push('bookmark');
            }
        }
        //
        if (this.parentSearcher.enableActionBarClipboard) {
            // copy to clipboard action
            actionNames.push('copyClipboard');
        }

        //
        if (this.parentSearcher.enableActionBarAnchor) {
            // anchor button only for POI items (not search items) that have a point to measure from
            // (not an unlisted address; see MapApp.actionOnItemToggleAnchor)
            if (![PTYPES.SEARCH, PTYPES.PDFLEAD].includes(item.ptype) && itemHasPointGeometry(item)) {
                actionNames.push('anchor');
            }
        }

        //
        if (this.parentSearcher.enableActionBarRead && (item.ptype!=PTYPES.SEARCH)) {
            // read action
            actionNames.push('readLead');
        }

        //
        if (this.getStateManager().getShowExtraActionButtons()) {
            // external geolocate actions (the menu's only entries): on geographic worlds, for Point items
            if (![PTYPES.SEARCH, PTYPES.PDFLEAD].includes(item.ptype) && this.mapApp?.canGeolocateItemExternally(item)) {
                actionNames.push('extraActionsMenu');
            }
        }

        //
        if (this.parentSearcher.enableActionBarDelete) {
            // edit notes for bookmaps and saved searches
            actionNames.push('delete');
        }
        if (this.parentSearcher.enableActionBarLaunch) {
            // edit notes for bookmaps and saved searches
            if (item.ptype == PTYPES.SEARCH) {
                actionNames.push('launchSearch');
            } else {
                actionNames.push('launchItem');
            }
        }


        // build the div
        let html = `
            <div class="d-flex justify-content-center gap-2 mt-1">
            `;

        for (const actionName of actionNames) {
            const config = this.getActionConfig()[actionName];
            const isActive = config.checkStateIsActive(item, uid);
            const display = this.getActionDisplay(actionName, isActive);
            html += `<button class="btn btn-outline-secondary btn-sm" data-action="${actionName}" title="${display.title}">
                    <i class="${display.icon}"></i>
                </button>`;
            }

        html += '</div>';
        return html;
    }




































    // ==========================================
    // CANDIDATES FOR PROMOTION TO SEARCH METHODS
    // ==========================================



 





















    // ADDING NEW RESULT ITEMS -- see Searcher.addResultCopy / addResultRef














    // ==========================================
    // FILTERING AND FINDING
    // ==========================================

    /**
     * A copy of the whole list, in its stored order. The internal lists (bookmarks, history, saved
     * searches) always show every item: there is deliberately no display cap (user decision, Oct 2026 --
     * the 100-item cap that was here never took effect, and turning it on would hide a long list's tail).
     * @returns {Array} Array of results
     */
    getAllResultsCopy() {
        return [...this.results];
    }

    /**
     * Search results for items matching a query (every match; an empty query matches everything)
     * @param {Object} searchPack - Search pack with the query and the fields to match
     * @returns {Array} Array of matching results
     */
    filterResults(searchPack) {

        // default options that can AND SHOULD be overridden
        const { query, fields = RESULT_MANAGER_CONSTANTS.DEFAULT_FILTER_FIELDS } = searchPack;

        if (!query || query.trim().length === 0) {
            return this.getAllResultsCopy();
        }

        const normalizedQuery = query.toLowerCase().trim();
        const results = [];

        for (const item of this.results) {
            const matches = anyFieldMatches(item, fields, value => value.includes(normalizedQuery));

            if (matches) {
                results.push(item);
            }
        }

        return results;
    }


    /**
     * Filter results by fields list past to us with no limit
     * Convenience method for common filtering use case working with internal small result lists
     * @param {string} query - Search query
     * @param {Array} fields - list of fields to use
     * @returns {Array} Array of matching results
     */
    filterResultsByFields(searchPack, fields) {
        searchPack.fields = fields;
        return this.filterResults(searchPack);
    }




    /**
     * Find another item with same basic contents
     * ATTN: This can be simplified
     * @param {Object} itemSearch - item to search for
     * @param {boolean} flagRequireDataMatch - Whether to require data match
     * @returns {Object|null} Matching item or null
     */
    findResultWithDuplicateContent(itemSearch, flagRequireDataMatch) {
        // loop preliminary check based on basic labels (if all of these match then its a presumptive match)
        const fields = FIELDS_PRESUMPTIVE_MATCH_FOR_DUPEADD;
        const ignoreFields = FIELDS_IGNORE_FOR_DUPEADD;
        //
        for (const item of this.results) {
            const matches = fields.every(field => {
                return (item[field] == itemSearch[field]);
            });

            if (matches) {
                // we got a preliminary match
                if (flagRequireDataMatch) {
                    // check that ALL non-transient fields match
                    const bretv = compareItemsIgnoreFields(itemSearch, item, ignoreFields);
                    if (bretv) {
                        // yes full match
                        return item;
                    } else {
                        // did not match data
                        // console.log(`findResultWithDuplicateContent preliminary fail "${JSON.stringify(itemSearch)}" vs "${JSON.stringify(item)}".`);
                    }
                } else {
                    // good enough
                    return item;
                }
            }
        }

        // not found
        return null;
    }






    // ==========================================
    // REMOVING RESULTS ETC
    // ==========================================

    /**
     * Clears all results and storage.
     * Also called when a blank or too-short search is performed on the database
     * (not for internal item filtering).
     */
    clearResultList() {
        // clear the result list; it's up to caller to save state, etc.
        //  display
        const searchResult = {
            results: []
        }
        this.setAllResultsFromSearchPack(searchResult, buildEmptySearchPack());
    }




    /**
     * Removes an item from the results array by its uid.
     * Does not re-render or update storage; caller is responsible for those operations.
     * @param {string} uid - Item uid to remove
     * @param {boolean} flagUpdateStorage - Whether storage should be updated (handled by caller)
     * @returns {boolean} True if an item was removed
     */
    removeResultById(uid, flagUpdateStorage) {
        const beforeLength = this.results.length;
        this.results = this.results.filter(item => item.uid !== uid);
        if (this.results.length < beforeLength) {
            return true;
        }
        return false;
    }
























    // ==========================================
    // ITEM RENDERING 1
    // ==========================================



    /**
     * Renders the specified results array to the content area.
     * This is the MAIN function that does all the rendering
     * Updates header count, handles empty states, and sets up click handlers.
     * Preserves selection state across re-renders.
     * @param {Array} resultsToDisplay - Array of results to render
     * @private
     */
    renderTheseResults(resultsToDisplay, searchPack) {
        // Update result header with count info, etc.
        this.updateResultHeaderWithCountAndErrorEtc(searchPack);

        // Show/hide Clear All button based on result count (only show if more than 1 result)
        const resultCount = resultsToDisplay.length;
        if (this.config.showClearAllButton && this.clearAllBtnEl) {
            this.clearAllBtnEl.style.display = resultCount > 0 ? 'inline-block' : 'none';
        }

        if (!this.contentEl) {
            return;
        }

        // Render results or empty message
        // Save currently selected item's uid before re-rendering
        const previouslySelectedUid = this.getSelectedResultUid();
        if (resultCount === 0) {
            if (!this.getHasSearched()) {
                // Initial state - no search performed yet
                if (this.getHasSearchedWaitingOnEnter()) {
                    this.contentEl.innerHTML = ``;
                } else {
                    this.contentEl.innerHTML = `
                        <div class="${CLASS_LISTER_EMPTY} text-muted text-center p-4">
                            ${this.config.emptyMessage}
                        </div>
                    `;
                }
            } else {
                // Search performed but no results
                this.contentEl.innerHTML = `
                    <div class="${CLASS_LISTER_EMPTY} text-muted text-center p-4">
                        ${this.config.noResultsMessage}
                    </div>
                `;
            }
        } else {
            // Has results - render them
            const itemsHtml = this.doRenderResultsToHtml(resultsToDisplay);

            // (item clicks/keys are handled by the delegated listeners bound once in the constructor)
            this.contentEl.innerHTML = `<div class="lister-items">${itemsHtml}</div>`;

            // Restore selection state if the previously selected item still exists; because we are RESTORING a view we want to scroll to but NOT smooth
            if (previouslySelectedUid) {
                const flagScrollIntoView = true;
                const flagFromMarkerClick = false;
                const flagSmoothAnimate = false;
                const flagFlashMarker = false;
                this.setSelectedResultOnListerByUid(previouslySelectedUid, flagScrollIntoView, flagFromMarkerClick, flagSmoothAnimate, flagFlashMarker);
            }
        }
    }



    /**
     * Actual loop rendering results and producing html.
     * Identifies items that should be rendered indented under a parent item,
     * with their dName and/or address stripped of common prefixes.
     * @param {Array} resultsToDisplay - Array of result items to render
     * @returns {string} HTML string of all rendered items
     */
    doRenderResultsToHtml(resultsToDisplay) {
        const optionNestDuplexes = true;
        //
        let itemsHtml = '';
        let potentialParent = null;
        const searcher = this.parentSearcher;

        searcher.updateShowExtraPcatInfo();

        for (let index = 0; index < resultsToDisplay.length; index++) {
            const item = resultsToDisplay[index];
            let itemToRender = item;
            let shouldIndent = false;
            const dName = item.dNameCustom ?? item.dName;
            let overrideDname;
            const ptype = item.ptype;
            let potentialParentDName;
            let nextItemDname;
            // Split dName at last '-' or '(' character
            const { baseName, suffix } = (ptype!='person') ? splitDNameIntoBaseAndSuffix(item.customBase, dName) : {baseName:dName, suffix:''};


            // PARENT INDENTING
            if (this.config.supportsIndent) {

                // Case 1: Check if base name matches potentialParent's full dName
                if (potentialParent && suffix) {
                    potentialParentDName = potentialParent.dNameCustom ?? potentialParent.dName;
                    if (baseName === potentialParentDName) {
                        shouldIndent = true;
                        // is it ALSO share same base address? if so fix like below
                        if (item.address && item.address === potentialParent.address &&
                            item.apt && !potentialParent.apt) {
                            itemToRender = { ...item, indentFlag: true, dNameOverride: suffix, addressOverride: '' };
                        } else {
                            itemToRender = { ...item, indentFlag: true, dNameOverride: suffix };
                        }
                    }
                }

                // Case 2: Check if item shares address with parent (parent has blank apt, item has non-blank apt)
                if (!shouldIndent && potentialParent) {
                    if (item.address && item.address === potentialParent.address &&
                        item.apt && !potentialParent.apt) {
                        itemToRender = { ...item, indentFlag: true, addressOverride: '' };
                        shouldIndent = true;
                    }
                }

                let nextItem;
                if (!shouldIndent) {
                    nextItem = index + 1 < resultsToDisplay.length ? resultsToDisplay[index + 1] : null;
                }

                // Case 3: Check for siblings without a common parent header
                if (!shouldIndent && suffix) {
                    if (nextItem) {
                        nextItemDname = nextItem.dNameCustom ?? nextItem.dName;
                        const { baseName: nextBaseName, suffix: nextSuffix } = splitDNameIntoBaseAndSuffix(nextItem.customBase, nextItemDname);

                        // If next item's dName starts with current item's baseName and both have suffixes, they're siblings
                        if (nextSuffix && (nextItemDname.startsWith(baseName) || nextBaseName==baseName)) {
                            // Create a header item for the group
                            const headerItem = {
                                ptype: 'header',
                                pcat: item.pcat,
                                pcatg: 'folder',
                                dName: baseName,
                                //address: item.address,
                                apt: ''
                            };

                            // Render header first
                            itemsHtml += this.renderSmartItem(headerItem, -1);

                            // Set header as the potential parent
                            potentialParent = { dName: baseName, address: item.address, apt: '' };

                            // Indent the current item under the header
                            itemToRender = { ...item, indentFlag: true, dNameOverride: suffix };
                            shouldIndent = true;
                        }
                        else {
                            // timeStamp
                            //alert(JSON.stringify(nextItem))
                            //alert("base ='"+baseName+"' and nextBaseName = '"+nextBaseName+"'.")
                        }
                    }
                }

                // Case 4: siblings at same address without parent apt
                if (!shouldIndent && nextItem && optionNestDuplexes && nextItem.address == item.address && nextItem.apt && item.apt && item.htype=='D' && nextItem.htype=='D') {
                    // they are at same address, but both have apt entries
                    // Create a header item for the group, whose NAME is the ADDRESS
                    const headerItem = {
                        ptype: 'header',
                        dName: 'Domicile at ' + item.address,
                        pcat: 'Apartments',
                        pcatg: 'folder',
                        //address: item.address,
                        apt: ''
                    };

                    // Render header first
                    itemsHtml += this.renderSmartItem(headerItem, -1);

                    // Set header as the potential parent
                    potentialParent = { dName: item.address, address: item.address, apt: '' };

                    // Indent the current item under the header
                    itemToRender = { ...item, indentFlag: true, addressOverride: '' };
                    shouldIndent = true;
                }

                // If not indented, this item becomes the new potential parent
                if (!shouldIndent) {
                    potentialParent = item;
                }
            }

            // Render the item (original or modified copy)
            itemsHtml += this.renderSmartItem(itemToRender, index);
        }

        return itemsHtml;
    }






    // ==========================================
    // ITEM RENDERING 2
    // ==========================================


    /**
     * Renders an item using the appropriate renderer based on item type.
     * @param {Object} item - The item to render
     * @param {number} index - Item index in the list
     * @returns {string} HTML string
     */
    renderSmartItem(item, index) {
        const searcher = this.parentSearcher;
        const options = {
            enableCornerDelete: searcher.enableCornerDelete,
            enableRunSearchButton: searcher.enableRunSearchButton,
            showAds: searcher.showAds,
            getAdUrl: searcher.showAds ? (dName) => this.mapApp.getUrlToYellowAdFromDname(dName) : null,
            calcOtherImageUrl: (relPath) => this.mapApp.getAuxDataManager().computeUrlForOtherImage(relPath),
        };

        const ptype = item.ptype;
        if (ptype === PTYPES.SEARCH) {
            return this.renderSmartItemSearch(item, index, options);
        } else if ((ptype === PTYPES.PERSON) || (ptype === PTYPES.YELLOW) || (ptype === PTYPES.POI) || (ptype === PTYPES.ACTION)) {
            return this.renderSmartItemPoi(item, index, options);
        } else if (ptype === PTYPES.CUSTOM) {
            return this.renderSmartItemPoi(item, index, options);
        } else if (ptype === PTYPES.HEADER) {
            return this.renderSmartItemHeader(item, index, options);
        } else if (ptype === PTYPES.PDFLEAD) {
            return this.renderSmartItemPdfLead(item, index, options);
        } else {
            // ATTN: for now just render it generically?
            // console.log(`CasebookMap WARNING: got ptype unknown in rendering (bad serializtion?); PTYPE: "${ptype}; full item = "${JSON.stringify(item)}".`);
            return this.renderSmartItemUnknown(item, index, options);
            // throw  Error(`Unknown ptype "${ptype}".`);
        }
    }


    /**
     * Builds the markup of one result card. THE one place the card structure lives and the one
     * place its fields are escaped: every *Text field, adUrl and uid is plain text (POI data, a
     * typed or imported search query, a bookmark label, a pdf outline title, ...) and is escaped
     * here; only the *Html fields are markup, and those are built by this file's own code.
     * @param {Object} card
     * @param {string} card.ptypeclass - css classes for the card (built from the item's ptype, which is
     *   data, so escaped here too)
     * @param {number} card.index - item index (data-index)
     * @param {string} card.uid - item uid (data-uid)
     * @param {string} [card.deleteButtonHtml=''] - corner delete button markup
     * @param {string} [card.iconHtml=''] - left icon column markup (see renderCardIconImgHtml)
     * @param {string} [card.dNameText=''] - main name line
     * @param {string} [card.userLabelText=''] - optional user label, shown quoted after the name
     * @param {string|null} [card.addressText=null] - address line text; null omits the line
     * @param {string} [card.anchorDistanceHtml=''] - anchor distance markup, after the address
     * @param {string} [card.leadText=''] - lead badge text
     * @param {string[]} [card.pcatLines=[]] - right column category / time lines (blank ones dropped)
     * @param {boolean} [card.alwaysShowPcat=false] - emit the (possibly empty) pcat box even with no lines
     * @param {string|null} [card.adUrl=null] - ad image url (right column), with card.adAltText
     * @param {string} [card.adAltText=''] - ad image alt text
     * @param {string|null} [card.regionLocText=null] - right column region/location text; null omits it
     * @returns {string} HTML string
     * @private
     */
    renderCardHtml(card) {
        const {
            ptypeclass, index, uid,
            deleteButtonHtml = '', iconHtml = '',
            dNameText = '', userLabelText = '',
            addressText = null, anchorDistanceHtml = '',
            leadText = '',
            pcatLines = [], alwaysShowPcat = false,
            adUrl = null, adAltText = '',
            regionLocText = null,
        } = card;

        const userLabelHtml = userLabelText ? ` <span class="userLabel">"${escapeHtml(userLabelText)}"</span>` : '';
        const addressHtml = (addressText === null) ? '' : `<div class="poi-address">${escapeHtml(addressText)} ${anchorDistanceHtml}</div>`;
        const shownPcatLines = pcatLines.filter(line => line);
        const pcatHtml = (shownPcatLines.length > 0 || alwaysShowPcat)
            ? `<div class="poi-pcat">${shownPcatLines.map(line => escapeHtml(line)).join('<br/>')}</div>`
            : '';
        const adImageHtml = adUrl ? `<div class="poi-ad"><img src="${escapeHtml(adUrl)}" alt="${escapeHtml(adAltText)}"/></div>` : '';
        const regionLocHtml = (regionLocText === null) ? '' : `<div class="poi-regionloc">${escapeHtml(regionLocText)}</div>`;

        // tabindex + pointer cursor make the card keyboard-focusable; the click/dblclick/keydown
        // handling is ONE delegated set of listeners on the container (bindDelegatedItemHandlers)
        return `
            <div class="${CLASS_LISTER_ITEM} list-group-item list-group-item-action poi-card ${escapeHtml(ptypeclass)}" data-index="${index}" data-uid="${escapeHtml(uid || '')}" tabindex="0" style="cursor: pointer;">
                ${deleteButtonHtml}
                ${iconHtml}
                <div class="poi-card-main">
                    <div class="poi-dname">${escapeHtml(dNameText)}${userLabelHtml}</div>
                    ${addressHtml}
                    <span class="poi-lead badge bg-secondary">${escapeHtml(leadText)}</span>
                </div>
                <div class="poi-card-right">
                    ${pcatHtml}
                    ${adImageHtml}
                    ${regionLocHtml}
                </div>
            </div>
        `;
    }


    /**
     * Builds the left icon column of a card from an image url ('' when there is no icon).
     * @param {string|null} iconSrc - Image url
     * @returns {string} HTML string
     * @private
     */
    renderCardIconImgHtml(iconSrc) {
        return !iconSrc ? '' : `<div class="poi-card-icon"><img src="${escapeHtml(iconSrc)}"/></div>`;
    }


    /**
     * Builds the corner delete button shown on cards of searchers with enableCornerDelete.
     * @param {Object} item - The item being rendered
     * @param {Object} options - Rendering options (enableCornerDelete)
     * @returns {string} HTML string ('' when the searcher has no corner delete)
     * @private
     */
    renderCardDeleteButtonHtml(item, options) {
        return options.enableCornerDelete
            ? HtmlBuilder.deleteButtonPositioned(item.uid, 'Delete from history')
            : '';
    }


    /**
     * Renders a search item (saved search, history result).
     * @param {Object} item - The search item to render
     * @param {number} index - Item index in the list
     * @param {Object} options - Rendering options
     * @returns {string} HTML string
     */
    renderSmartItemSearch(item, index, options = {}) {
        const searcher = this.parentSearcher;
        const deleteButton = this.renderCardDeleteButtonHtml(item, options);

        const runSearchButton = options.enableRunSearchButton
            ? HtmlBuilder.inlineActionButton({
                icon: 'bi-play-circle-fill',
                title: 'Run this search now',
                uid: item.uid,
                actionClass: 'saved-search-execute-btn'
            })
            : '';

        // Build fields -- note that we name these search history fields based on reusing code for renderer.
        const ptype = item.ptype;
        let ptypeclass = `ptypeclass_${ptype}`;
        const totalCount = item.searchTotalCount || 0;
        const searchType = item.searchType;
        const query = item.searchQuery;
        const userLabel = item.userLabel;
        const dNameOveride = item.dNameCustom ?? item.dName;

        // dynamic fields
        // (own keys only: searchType is stored data, and "constructor" would find Object's function)
        const lead = getOwnValue(SEARCH_TYPE_LABELS, searchType) || searchType;
        let dName;
        if (dNameOveride) {
            dName = dNameOveride;
        } else {
            if (!query) {
                dName = `[blank ${searchType} search]`;
            } else {
                dName = query;
            }
        }

        if (userLabel) {
            // a tricky to add query to userLabel
            if (userLabel.endsWith(":")) {
                dName = userLabel + ' ' + dName;
            } else {
                dName = userLabel;
            }
        }

        // a searchTotalCount of -1 means "no meaningful count" (e.g. curated
        // specialYellow entries); suppress the results-count line in that case
        const address = (totalCount === -1) ? '' : `${totalCount} results`;

        const timeStampField = this.getTimeStampField();
        let timeStamp = timeStampField ? item[timeStampField] : null;
        const timeAgo = timeStamp ? formatTimeAgo(timeStamp) : '';
        //console.log(`TIMESTAMP FIELD is ${timeStampField} value is ${timeStamp} and ago = ${timeAgo}.`)

        //
        let regionloc;
        if (item.selectedRegion) {
            regionloc = item.selectedRegion;
        } else {
            regionloc = '--';
        }

        // extra classes
        //
        // indent class if appropriate
        const indentFlag = item.indentFlag;
        if (indentFlag) {
            ptypeclass += ' itemIndent';
        }
        // close button class
        if (searcher.enableCornerDelete) {
            ptypeclass +=' cornerbut';
        }
        // no results-count line; restore the vertical gap it would have provided
        if (!address) {
            ptypeclass += ' noAddressLine';
        }

        return this.renderCardHtml({
            ptypeclass,
            index,
            uid: item.uid,
            deleteButtonHtml: deleteButton,
            // the icon column holds the run-search button (empty column when there is none)
            iconHtml: '<div class="poi-card-icon">' + runSearchButton + '</div>',
            dNameText: dName,
            addressText: address ? address : null,
            leadText: lead,
            pcatLines: [timeAgo],
            alwaysShowPcat: true,
            regionLocText: regionloc,
        });
    }


    /**
     * Renders a POI item (place or person).
     * @param {Object} item - The POI item to render
     * @param {number} index - Item index in the list
     * @param {Object} options - Rendering options
     * @returns {string} HTML string
     */
    renderSmartItemPoi(item, index, options = {}) {
        const searcher = this.parentSearcher;
        // helper texts
        const deleteButton = this.renderCardDeleteButtonHtml(item, options);

        // helper variables
        const mapApp = searcher.getMapApp();
        const ptype = item.ptype;
        let ptypeclass = `ptypeclass_${ptype}`;

        // build main display fields
        // An imported custom pin needs only a uid and a point, so its name and address may both be
        // missing: undefined == undefined then sent the Canvas test below into address.startsWith,
        // which threw on every render, so the bookmark list never drew again (second code study
        // 12.2, Oct 2026). Same guard as calculateHoverTextHtml (utils/nynfuncs.js).
        let dName = item.dNameOverride ?? item.dNameCustom ?? item.dName;
        if (typeof dName !== 'string') dName = '';
        let address = formatAddressForItemNicelyCheckOverride(item, false);
        if (typeof address !== 'string') address = '';
        // a non-breaking space between region and location (was the &nbsp; entity in the markup)
        const regionloc = item.loclabel ? `${item.jregion}\u00A0${item.loclabel}` : item.jregion;
        const lead = item.lead;
        //const pcat = searcher.getShowExtraPcatInfo() ? (item.pcat?.toUpperCase() ?? '&nbsp;') : '&nbsp;';
        const pcat = searcher.getShowExtraPcatInfo() ? (item.pcat?.toUpperCase() ?? '') : '';
        let itemIcon = mapApp.calcIconPathForItem(item);
        const timeStampField = this.getTimeStampField();
        let timeStamp = timeStampField ? item[timeStampField] : null;
        const timeAgo = timeStamp ? formatTimeAgo(timeStamp) : '';
        // console.log(`TIMESTAMP FIELD is ${timeStampField} value is ${timeStamp} and ago = ${timeAgo}.`)

        // the region/location column is dropped when the lead already says the same thing
        const showRegionLoc = (lead != item.jregion + ' ' + item.loclabel);


        // for things like Canvas
        if (dName && dName == address) {
            if (address.startsWith('Canvas area around ')) {
                dName = 'Canvas ' + address.substring(19);
                address = 'Special action';
            } else {
                address = '';
            }
        }

        // compute ads images if any
        let adUrl = null;
        if (item.image) {
            adUrl = options.calcOtherImageUrl(item.image);
        } else {
            if (ptype === PTYPES.YELLOW && options.showAds) {
                adUrl = options.getAdUrl(dName);
            }
        }

        // extra classes
        //
        // indent class if appropriate
        const indentFlag = item.indentFlag;
        if (indentFlag) {
            ptypeclass += ' itemIndent';
        }
        // close button class
        if (searcher.enableCornerDelete) {
            ptypeclass +=' cornerbut';
        }

        // compute anchor distance
        const anchorDistanceString = searcher.getMapApp().calcDistanceToAnchorAsHtmlString(item);
        const anchorDistanceHtml = anchorDistanceString ? `<span class="poi-anchor-dist">${anchorDistanceString}</span>` : '';

        // dont show dname if its MAP_CONSTANTS.USER_BOOKMARK_NAME and we have an override
        if (dName == MAP_CONSTANTS.USER_BOOKMARK_NAME && item.userLabel) {
            dName = "";
        }

        // and now render the card (the relevant css classes are in css/lister.css)
        return this.renderCardHtml({
            ptypeclass,
            index,
            uid: item.uid,
            deleteButtonHtml: deleteButton,
            iconHtml: this.renderCardIconImgHtml(itemIcon),
            dNameText: dName,
            userLabelText: item.userLabel,
            addressText: address ?? '',
            anchorDistanceHtml,
            leadText: lead,
            pcatLines: [pcat, timeAgo],
            adUrl,
            adAltText: `${dName} advert`,
            regionLocText: showRegionLoc ? (regionloc ?? '') : null,
        });
    }





    /**
     * Renders a generic pdf lead reference
     * @param {Object} item - The POI item to render
     * @param {number} index - Item index in the list
     * @param {Object} options - Rendering options
     * @returns {string} HTML string
     */

    renderSmartItemPdfLead(item, index, options = {}) {
        const searcher = this.parentSearcher;
        // helper texts
        const deleteButton = this.renderCardDeleteButtonHtml(item, options);

        // helper variables
        const mapApp = searcher.getMapApp();
        const ptype = item.ptype;
        let ptypeclass = `ptypeclass_${ptype}`;

        // build main display fields
        let dName = item.dNameOverride ?? item.dNameCustom ?? item.dName ?? item.lead;
        let address = item.address;
        const lead = item.lead;

        const iconRelativePath = 'icons/picto/file-pdf-box.svg';
        let itemIcon = mapApp.calcIconPathForRelativeImagePath(iconRelativePath);

        const timeStampField = this.getTimeStampField();
        let timeStamp = timeStampField ? item[timeStampField] : null;
        const timeAgo = timeStamp ? formatTimeAgo(timeStamp) : '';

        // close button class
        if (searcher.enableCornerDelete) {
            ptypeclass +=' cornerbut';
        }

        // and now render the card (the relevant css classes are in css/lister.css)
        return this.renderCardHtml({
            ptypeclass,
            index,
            uid: item.uid,
            deleteButtonHtml: deleteButton,
            iconHtml: this.renderCardIconImgHtml(itemIcon),
            dNameText: dName,
            userLabelText: item.userLabel,
            addressText: address ?? '',
            leadText: lead,
            pcatLines: [timeAgo],
        });
    }












    /**
     * Renders a header item for grouping.
     * @param {Object} item - The header item to render
     * @param {number} index - Item index in the list
     * @param {Object} options - Rendering options
     * @returns {string} HTML string
     */
    renderSmartItemHeader(item, index, options = {}) {
        const searcher = this.parentSearcher;
        const dName = item.dNameCustom ?? item.dName;
        const ptype = item.ptype;
        const ptypeclass = `ptypeclass_${ptype}`;

        const mapApp = searcher.getMapApp();
        let itemIcon = mapApp.calcIconPathForItem(item);
        let itemIconHtml = this.renderCardIconImgHtml(itemIcon);

        return `
            <div class="lister-vheader ${escapeHtml(ptypeclass)}" data-index="${index}">
            ${itemIconHtml}
                <span class="vheaderDname">${escapeHtml(dName)}</span>
            </div>
        `;
    }


    /**
     * Renders an item with an unknown ptype as a generic debug view.
     * Shows the item's UID and full JSON representation.
     * @param {Object} item - The item to render
     * @param {number} index - Item index in the list
     * @param {Object} options - Rendering options
     * @returns {string} HTML string
     */
    renderSmartItemUnknown(item, index, options = {}) {
        const ptype = item.ptype;
        const ptypeclass = `ptypeclass_${ptype}`;

        // the JSON dump is text like everything else (it can come from an imported file)
        return this.renderCardHtml({
            ptypeclass,
            index,
            uid: item.uid,
            deleteButtonHtml: this.renderCardDeleteButtonHtml(item, options),
            dNameText: `UID: ${item.uid}`,
            addressText: JSON.stringify(item),
            leadText: '--',
        });
    }





    // ==========================================
    // REFRESHING APPEARANCE
    // ==========================================


    




    /**
     * Updates the appearance of an item when its state changes elsewhere.
     * Currently handles refreshing the action bar icons for the active item.
     * @param {string} uid - Unique identifier of the item to update
     */
    updateAppearanceOfItemActionBarByUidOnLister(uid) {
        // ATTN: We dont handle arbitrary changes here, but only some special cases, for example where the item is ACTIVE and is shown icons of different states
        // In future we might have custom appearances of items that MIGHT change based on some bookmark state (e.g. comment/label) that we might want to force an update of here
        this.refreshSelectedActiveItemActionBarByUid(uid)
    }


    /**
     * Refreshes the action bar icons for the currently selected/active item.
     * Updates button icons and titles to reflect the current state (e.g., bookmarked status).
     * Call this when external state changes might affect the action bar display.
     */
    refreshSelectedActiveItemActionBarByUid(targetUid) {
        // Get the currently selected item element
        const selectedItemEl = this.container.querySelector(`.${RESULT_MANAGER_CONSTANTS.SELECTED_RESULT_CLASS}`);
        if (!selectedItemEl) return;

        // Get the action bar within the selected item
        const actionBar = selectedItemEl.querySelector(`.${RESULT_MANAGER_CONSTANTS.ACTION_BAR_CLASS}`);
        if (!actionBar) return;

        // Get the uid and item data
        const uid = selectedItemEl.dataset.uid;
        if (!uid) return;

        // don't bother if it doesn't match our uid
        if (targetUid != uid) return;

        const item = this.results.find(r => r.uid === uid);

        // Update each action button's icon and title based on current state
        const actionConfig = this.getActionConfig();
        const buttons = actionBar.querySelectorAll('[data-action]');

        buttons.forEach(button => {
            const action = button.dataset.action;
            const config = actionConfig[action];

            if (config) {
                const isActive = config.checkStateIsActive(item, uid);
                const display = this.getActionDisplay(action, isActive);

                // Update the icon
                const icon = button.querySelector('i');
                if (icon) {
                    icon.className = display.icon;
                }

                // Update the title
                button.title = display.title;
            }
        });
    }









}
